import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { refreshMeRole } from '../hooks/useMeRole';

/**
 * Invite-only vendor onboarding: /invite/:token
 */
export default function InviteRedeem() {
  const { token } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError('Invalid invite link.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/invites/${encodeURIComponent(token)}`, {});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Invite not found.');
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleJoin = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api(`/api/invites/${encodeURIComponent(token)}/redeem`, {
        getToken,
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      refreshMeRole();
      navigate('/profile', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        Loading invite…
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Vendor invite</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold">Join as a vendor</h1>
      <p className="text-lg text-slate-600 dark:text-slate-400">
        You’ve been invited to sell at <strong>{preview?.marketName}</strong>
        {typeof preview?.slotsRemaining === 'number' && (
          <> · {preview.slotsRemaining} slot{preview.slotsRemaining === 1 ? '' : 's'} remaining</>
        )}
        .
      </p>

      {error && (
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      )}

      {!preview?.valid && (
        <p className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 text-amber-900 dark:text-amber-100">
          This invite is no longer valid. Ask your market owner for a new link.
        </p>
      )}

      <SignedOut>
        <p className="text-slate-600 dark:text-slate-400">
          Sign in (or create an account) to accept this invite and set up your booth.
        </p>
        <SignInButton mode="modal" forceRedirectUrl={window.location.href}>
          <button
            type="button"
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Sign in to continue
          </button>
        </SignInButton>
      </SignedOut>

      <SignedIn>
        <button
          type="button"
          onClick={handleJoin}
          disabled={submitting || !preview?.valid}
          className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Joining…' : 'Accept invite & join'}
        </button>
      </SignedIn>
    </div>
  );
}
